import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, MapPin, Award, Mail, Cpu, Globe, Scale, MessageSquare } from 'lucide-react';
import { businessConfig as BusinessInfo } from '../config/businessConfig'; 
import ReferenceLibraryLink from './ReferenceLibraryLink';

/**
 * MICHEALS CONSULTING - PRINCIPAL FOOTER
 * ROLE: Legal Compliance & Authority Reinforcement
 * VERSION: 4.1.0 (Build 2026.06.25 - Consultative Finalization)
 */
const Footer = ({ sessionId }) => {
  return (
    <footer className="bg-stone-950 text-white pt-24 pb-12 border-t border-stone-800">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
          
          {/* Brand Identity & Strategic Briefing */}
          <div className="space-y-6">
            <h3 className="text-xl font-serif tracking-tight">
              {BusinessInfo.brandSpelling} <span className="italic text-stone-500">Consulting Solutions</span>
            </h3>
            <p className="text-stone-400 leading-relaxed text-[13px] font-light italic">
              "Providing the forensic logic and architectural stewardship that ensures your infrastructure is resilient, recoverable, and right-sized."
            </p>
            
            <div className="pt-4">
              <h4 className="font-bold text-stone-500 mb-4 uppercase tracking-[0.2em] text-[9px]">Reference Library</h4>
              <ReferenceLibraryLink />
            </div>

            <div className="pt-2 space-y-3 border-t border-stone-900 mt-4">
               <div className="flex items-center gap-4 text-stone-500 hover:text-amber-500 transition-colors group">
                  <Mail size={14} className="group-hover:scale-110 transition-transform" />
                  <a href={`mailto:${BusinessInfo.email}`} className="text-[11px] font-mono tracking-wider lowercase">{BusinessInfo.email}</a>
               </div>
            </div>
          </div>

          {/* Corporate & Operations */}
          <div>
            <h4 className="font-bold text-amber-600 mb-8 uppercase tracking-[0.3em] text-[10px]">Reach & Operations</h4>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <MapPin size={16} className="text-amber-700 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[12px] font-bold text-stone-200 uppercase tracking-widest">Base of Operations</p>
                  <p className="text-[11px] text-stone-400 mt-1">Charlotte & Huntersville, NC // Fort Mill, SC</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MessageSquare size={16} className="text-amber-700 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[12px] font-bold text-stone-200 uppercase tracking-widest">Direct Briefing</p>
                  <p className="text-[11px] text-stone-400 mt-1">Secure communication channel for strategic oversight.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Globe size={16} className="text-amber-700 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[12px] font-bold text-stone-200 uppercase tracking-widest">Global Stewardship</p>
                  <p className="text-[11px] text-stone-400 mt-1">Virtual Forensic Audits & Remote Technical Governance.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Service Architecture */}
          <div>
            <h4 className="font-bold text-amber-600 mb-8 uppercase tracking-[0.3em] text-[10px]">The Protocol</h4>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <Cpu size={18} className="text-amber-700 mt-1 shrink-0" />
                <div>
                  <p className="text-[12px] font-bold text-stone-200 uppercase tracking-widest">Infrastructure Design</p>
                  <p className="text-[11px] text-stone-500 leading-relaxed mt-2">Architectural-level system blueprinting and digital logic specification.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Scale size={18} className="text-amber-700 mt-1 shrink-0" />
                <div>
                  <p className="text-[12px] font-bold text-stone-200 uppercase tracking-widest">Forensic Verification</p>
                  <p className="text-[11px] text-stone-500 leading-relaxed mt-2">Independent verification of third-party contractor builds.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Standing */}
          <div>
            <h4 className="font-bold text-amber-600 mb-8 uppercase tracking-[0.3em] text-[10px]">Professional Standing</h4>
            <div className="space-y-5">
              <div className="flex items-center gap-4 text-stone-300">
                <Award size={18} className="text-amber-600/40" />
                <span className="text-[13px] font-light italic text-stone-400">Independent Technical Counsel</span>
              </div>
              <div className="flex items-center gap-4 text-stone-300">
                <Shield size={18} className="text-amber-600/40" />
                <span className="text-[13px] font-light italic text-stone-400">Liability & E&O Covered</span>
              </div>
              <div className="pt-6">
                <div className="inline-block border border-amber-900/40 bg-amber-900/10 px-4 py-2 text-[9px] text-amber-500 uppercase tracking-[0.4em] font-black rounded-sm shadow-[inset_0_0_10px_rgba(217,119,6,0.1)]">
                  Consultation Only Model
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Transparency & Disclosure */}
        <div className="pt-12 border-t border-stone-900 flex flex-col items-center gap-8 text-stone-600 text-[10px] uppercase tracking-[0.25em] text-center">
          <div className="flex flex-wrap justify-center gap-10 opacity-80 font-bold">
            <Link to="/about" className="hover:text-amber-600 transition-colors">The Firm</Link>
            <Link to="/infrastructure-audit" className="hover:text-amber-600 transition-colors">The Protocol</Link>
            <Link to="/book-consultation" className="hover:text-amber-600 transition-colors">Start a Briefing</Link>
            <Link to="/privacy" className="hover:text-amber-600 transition-colors">Privacy Protocol</Link>
          </div>

          <p className="font-medium tracking-[0.4em] text-stone-500">© {new Date().getFullYear()} {BusinessInfo.name}</p>
          
          <div className="max-w-4xl space-y-4 leading-relaxed opacity-40 px-6 normal-case text-[10px] font-light italic">
            <p>
              {BusinessInfo.name} functions exclusively as an architectural-level technical design and owner's representative firm. 
            </p>
            <p>
              Our methodology involves technical forensic auditing and system logic oversight. We do not provide physical labor. All field implementation must be performed by locally licensed partners. Micheals Infrastructure Advisory remains the independent technical steward of the design intent.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;